import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import MediaQuery from 'react-responsive';
import { CSSTransition } from 'react-transition-group';

import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import useApi from '../../../../hooks/useApi';
import AvatarUploader from '../../../AvatarUploader';
import {FirmEditLogoFormValidationSchema} from './validate';

const FirmEditLogoForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params: { id },
  } = props;

  const api = useApi();
  const [logo, setLogo] = useState();

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.FIRM_LOGO,
      { id },
      {
        handleSuccess: response => {
          setLogo({
            image:
              response && response.hasOwnProperty('file')
                ? new File([response.file], response.filename, {
                    type: response.type,
                  })
                : null,
            croppingRect: null,
          });
        },
      },
      { responseType: 'blob' }
    );
  }, []);

  const handleSubmit = async ({ Picture }, { setSubmitting, setErrors }) => {
    const obj = { Picture: Picture.image, ...Picture.croppingRect, id };

    api.post(ENDPOINT.FORM.SAVE.FIRM_LOGO, obj, {
      handleForbidden: handleError,
      handleError: response => {
        handleError(response, setErrors);
        setSubmitting(false);
      },
      handleSuccess: response => proceed(response),
    });
  };

  const handleSelectImage = (name, logo, { setFieldValue }) => {
    setFieldValue(name, logo);
  };

  const handleChangeImage = (name, logo, { setFieldValue }) => {
    setFieldValue(name, logo);
  };

  const handleRemoveImage = (name, logo, { setFieldValue, setFieldError }) => {
    setFieldError(name);
    setFieldValue(name, logo, false);
  };

  const preValidate = (name, event, { setFieldError }) => {
    const {
      target: {
        files: [file],
      },
    } = event;

    if (!file || file.size > 10 * 1024 * 1024) {
      event.target.value = '';
      setFieldError(name, 'The file is too big. Max file size is 10MB');
    }

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      event.target.value = '';
      setFieldError(name, 'This type of file is not supported');
    }
  };

  return logo ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={{ Picture: logo }}
          validationSchema={FirmEditLogoFormValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            isValid,
            values,
            errors,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldError,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}
              <MediaQuery query="(min-width: 481px)">
                <AvatarUploader
                  name="Picture"
                  image={values.Picture.image}
                  position={values.Picture.position}
                  scale={values.Picture.scale}
                  borderRadius={0}
                  size={300}
                  onChange={(n, a) =>
                    handleChangeImage(n, a, { setFieldValue })
                  }
                  onRemove={(n, a) =>
                    handleRemoveImage(n, a, {
                      setFieldValue,
                      setFieldError,
                    })
                  }
                  onSelect={(n, a) =>
                    handleSelectImage(n, a, { setFieldValue })
                  }
                  onBeforeSelect={(n, e) =>
                    preValidate(n, e, { setFieldError })
                  }
                />
              </MediaQuery>

              <MediaQuery query="(max-width: 480px)">
                <AvatarUploader
                  name="Picture"
                  image={values.Picture.image}
                  position={values.Picture.position}
                  scale={values.Picture.scale}
                  borderRadius={0}
                  size={200}
                  onChange={(n, a) =>
                    handleChangeImage(n, a, { setFieldValue })
                  }
                  onRemove={(n, a) =>
                    handleRemoveImage(n, a, {
                      setFieldValue,
                      setFieldError,
                    })
                  }
                  onSelect={(n, a) =>
                    handleSelectImage(n, a, { setFieldValue })
                  }
                  onBeforeSelect={(n, e) =>
                    preValidate(n, e, { setFieldError })
                  }
                />
              </MediaQuery>

              {errors.Picture && (
                <div className="form__error">{errors.Picture}</div>
              )}

              <div className="form__controls form__controls--compact">
                <MediaQuery query="(min-width: 481px)">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      disabled={!values.Picture.image || !isValid}
                      controlFunc={handleSubmit}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Upload"
                    />

                    <BasicButton
                      controlFunc={dismiss}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Cancel"
                    />
                  </BasicButtonGroup>
                </MediaQuery>

                <MediaQuery query="(max-width: 480px)">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      disabled={!values.Picture.image || !isValid}
                      controlFunc={handleSubmit}
                      btnWidth={120}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Upload"
                    />

                    <BasicButton
                      controlFunc={dismiss}
                      btnWidth={120}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Cancel"
                    />
                  </BasicButtonGroup>
                </MediaQuery>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <HorizontalLoader />
    </div>
  );
};

export default FirmEditLogoForm;
